import { type ChangeEvent, useState, type ReactNode  } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { partnerAccountTopupMutate } from '@/entities/partner';
import { useAccountLegalEntity } from '@/entities/account';
import { isHttpsPaymentLink } from '@/features/billing/model/paymentRedirect';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { TextField } from '@/shared/ui/lib/TextField';
import { CustomTypography } from '@/shared/ui/Typography';

type Method = 'card' | 'invoice';

interface TopUpDialogProps {
  open: boolean;
  onClose: () => void;
}

export function TopUpDialog({ open, onClose }: TopUpDialogProps): ReactNode {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntity } = useAccountLegalEntity(open);
  const [amount, setAmount] = useState('1000');
  const [method, setMethod] = useState<Method>('card');
  const [error, setError] = useState<string | null>(null);

  const createTopup = useMutation({
    ...partnerAccountTopupMutate(),
    onError: (err) => {
      setError(getTopupErrorMessage(err));
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['partner', 'account'] });
      qc.invalidateQueries({ queryKey: ['partner', 'account', 'transactions'] });
      onClose();
      if (result.paymentUrl) {
        if (!isHttpsPaymentLink(result.paymentUrl)) {
          console.warn('TopUpDialog: blocked non-https top-up link');
          return;
        }
        const opened = window.open(result.paymentUrl, '_blank', 'noopener,noreferrer');
        if (!opened) window.location.assign(result.paymentUrl);
      }
    },
  });

  const amountMajor = Number(amount);
  const hasLegalEntity = Boolean(
    legalEntity?.entity_type &&
    legalEntity?.name &&
    legalEntity?.inn &&
    (legalEntity.entity_type !== 'ooo' || legalEntity.kpp),
  );
  const showLegalEntityWarning = legalEntity !== undefined && !hasLegalEntity;
  const requiresLegalEntity = method === 'invoice';
  const isValid = Number.isFinite(amountMajor) && amountMajor > 0 && (!requiresLegalEntity || hasLegalEntity);
  const errorText = error ? `${t('common:affiliatePage.dialogs.topUp.payError')}: ${error}` : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('common:affiliatePage.dialogs.topUp.title') ?? ''}
      leftButtonSlot={<Button onClick={onClose} disabled={createTopup.isPending}>{t('common:buttons.back')}</Button>}
      rightButtonSlot={
        <Button
          variant="contained"
          disabled={!isValid || createTopup.isPending}
          onClick={() => {
            setError(null);
            createTopup.mutate({
              amountMinor: Math.round(amountMajor * 100),
              method,
            });
          }}
        >
          {method === 'invoice'
            ? t('common:affiliatePage.dialogs.topUp.createInvoice')
            : t('common:affiliatePage.dialogs.topUp.pay')}
        </Button>
      }
    >
      <Stack spacing={2} sx={{ minWidth: 420 }}>
        <TextField
          label={t('common:affiliatePage.dialogs.topUp.amountLabel')}
          placeholder="1000"
          value={amount}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
          fullWidth
        />
        <Stack spacing={0.5}>
          <span style={{ fontSize: 12, opacity: 0.7 }}>
            {t('common:affiliatePage.dialogs.topUp.methodLabel')}
          </span>
          <ToggleButtonGroup
            value={method}
            exclusive
            size="small"
            fullWidth
            onChange={(_, value: Method | null) => {
              if (value) setMethod(value);
            }}
          >
            <ToggleButton value="card">
              {t('common:affiliatePage.dialogs.topUp.methods.card')}
            </ToggleButton>
            <ToggleButton value="invoice">
              {t('common:affiliatePage.dialogs.topUp.methods.invoice')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {requiresLegalEntity && showLegalEntityWarning && (
          <CustomTypography variant="caption" color="error">
            {t('common:affiliatePage.dialogs.topUp.legalEntityRequired')}
          </CustomTypography>
        )}
        {errorText && (
          <CustomTypography variant="caption" color="error">
            {errorText}
          </CustomTypography>
        )}
      </Stack>
    </Dialog>
  );
}

function getTopupErrorMessage(err: unknown): string | null {
  const data = (err as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data;
  const detail = typeof data?.error === 'string' ? data.error : typeof data?.message === 'string' ? data.message : undefined;
  return detail || null;
}
